import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RealtimeClient } from '@openai/realtime-api-beta';
import { ItemType } from '@openai/realtime-api-beta/dist/lib/client.js';
import { WavRecorder, WavStreamPlayer } from '../lib/wavtools/index.js';
import { WavRenderer } from '../utils/wav_renderer';
import Blob from '../components/blob/Blob';
import { Button } from '../components/button/Button';
import { InterstitialPage } from './InterstitialPage';

import './ConsolePage.scss';

// import { isJsxOpeningLikeElement } from 'typescript';

/**
 * Type for all event logs
 */
interface RealtimeEvent {
  time: string;
  source: 'client' | 'server';
  count?: number;
  event: { [key: string]: any };
}

interface ConsolePageProps {
  remainingTime: null | number;
  setRemainingTime: React.Dispatch<React.SetStateAction<null | number>>;
  profilePic: any;
  setInterviewFeedback: React.Dispatch<React.SetStateAction<string>>;
}

export function ConsolePage({ remainingTime, setRemainingTime, profilePic, setInterviewFeedback }: ConsolePageProps) {

  const navigate = useNavigate();

  // Function to get all the transcribed text from the convo
  // so we can feed it into the review at the end
  const getConvoText = useCallback(() => {
    const convoElement = document.querySelector('.convo');
    if (convoElement) {
      return convoElement.textContent || '';
    }
    return '';
  }, []);

  const [jobDescription, setjobDescription] = useState('');
  const jobDescriptionRef = useRef(jobDescription);

  useEffect(() => {
    jobDescriptionRef.current = jobDescription;
  }, [jobDescription]);


  // Add state for toggling convo visibility
  const [isConvoVisible, setIsConvoVisible] = useState(false);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const receivedSessionUpdatedRef = useRef(false);

  /**
   * Instantiate:
   * - WavRecorder (speech input)
   * - WavStreamPlayer (speech output)
   * - RealtimeClient (API client)
   */
  const wavRecorderRef = useRef<WavRecorder>(
    new WavRecorder({ sampleRate: 24000 })
  );
  const wavStreamPlayerRef = useRef<WavStreamPlayer>(
    new WavStreamPlayer({ sampleRate: 24000 })
  );

	const realtimeClientRef = useRef<RealtimeClient | null>(null);

  /**
   * References for
   * - Rendering audio visualization (canvas)
   * - Autoscrolling event logs
   * - Timing delta for event log displays
   */
  const clientCanvasRef = useRef<HTMLCanvasElement>(null);
  const serverCanvasRef = useRef<HTMLCanvasElement>(null);
  // const eventsScrollHeightRef = useRef(0);
  // const eventsScrollRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<string>(new Date().toISOString());

  /**
   * All of our variables for displaying application state
   * - items are all conversation items (dialog)
   * - realtimeEvents are event logs, which can be expanded
   * - memoryKv is for set_memory() function
   * - coords, marker are for get_weather() function
   */
  const [items, setItems] = useState<ItemType[]>([]);
  const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([]);
  // const [expandedEvents, setExpandedEvents] = useState<{
  //   [key: string]: boolean;
  // }>({});
  const [isConnected, setIsConnected] = useState(false);
  const [canPushToTalk, setCanPushToTalk] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isInterstitialVisible, setIsInterstitialVisible] = useState(false);
  const [isPostInterviewVisible, setIsPostInterviewVisible] = useState(false);
  const [isConsoleVisible, setIsConsoleVisible] = useState(false);

  // const formatTime = useCallback((timestamp: string) => {
  //   const startTime = startTimeRef.current;
  //   const t0 = new Date(startTime).valueOf();
  //   const t1 = new Date(timestamp).valueOf();
  //   const delta = t1 - t0;
  //   const hs = Math.floor(delta / 10) % 100;
  //   const s = Math.floor(delta / 1000) % 60;
  //   const m = Math.floor(delta / 60_000) % 60;
  //   const pad = (n: number) => {
  //     let s = n + '';
  //     while (s.length < 2) {
  //       s = '0' + s;
  //     }
  //     return s;
  //   };
  //   return `${pad(m)}:${pad(s)}.${pad(hs)}`;
  // }, []);

  /**
   * Connect to conversation:
   * WavRecorder taks speech input, WavStreamPlayer output, client is API client
   */

  const [timer, setTimer] = useState(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);

  const startTimer = useCallback(() => {
    setTimer(0);
    const interval = setInterval(() => {
      setTimer((prev) => prev + 1);
    }, 1000);
    setTimerInterval(interval);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
  }, [timerInterval]);

  const connectConversation = useCallback(async (hasResume: boolean) => {
    // HACK: The `clientPath` string is deliberally misformatted.
    // See the comment in `wsHandler` of the backend for motivation
    const clientPath = encodeURI(`/aiproxy/v1/realtime?qparamsabuse=${hasResume}<|>${jobDescriptionRef.current}`)
    realtimeClientRef.current = new RealtimeClient({ url: clientPath });
    const realtimeClient = realtimeClientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;

    // Set state variables
    startTimeRef.current = new Date().toISOString();
    setIsConnected(true);
    setRealtimeEvents([]);
    if (realtimeClient) {
      setItems(realtimeClient.conversation.getItems());
    }

    // Connect to microphone
    await wavRecorder.begin();

    // Connect to audio output
    await wavStreamPlayer.connect();

    // TODO: client needs to wait for the session updated event before sending up any audio data.

    await realtimeClient?.connect();

    await wavRecorder.record((data) => {
      if (receivedSessionUpdatedRef.current) {
        console.log("Sending up audio");
        realtimeClient?.appendInputAudio(data.mono);
      } else {
        console.log("Ignorning audio");
      }
    })

    startTimer();

    // handle realtime events from client + server for event logging
    realtimeClient?.on('realtime.event', (realtimeEvent: RealtimeEvent) => {
      if (realtimeEvent.event.type == 'session.updated') {
        console.log("Gonna send up input audio now!!!!");
        receivedSessionUpdatedRef.current = true;
        // setReceivedSessionUpdated(true);
      }
      setRealtimeEvents((realtimeEvents) => {
        const lastEvent = realtimeEvents[realtimeEvents.length - 1];
        if (lastEvent?.event.type === realtimeEvent.event.type) {
          // if we receive multiple events in a row, aggregate them for display purposes
          lastEvent.count = (lastEvent.count || 0) + 1;
          return realtimeEvents.slice(0, -1).concat(lastEvent);
        } else {
          return realtimeEvents.concat(realtimeEvent);
        }
      });
    });
    realtimeClient?.on('error', (event: any) => console.error(event));
    realtimeClient?.on('conversation.interrupted', async () => {
      const trackSampleOffset = await wavStreamPlayer.interrupt();
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        await realtimeClient?.cancelResponse(trackId, offset);
      }
    });
    realtimeClient?.on('conversation.updated', async ({ item, delta }: any) => {
      const items = realtimeClient?.conversation.getItems();
      if (delta?.audio) {
        wavStreamPlayer.add16BitPCM(delta.audio, item.id);
      }
      if (item.status === 'completed' && item.formatted.audio?.length) {
        const wavFile = await WavRecorder.decode(
          item.formatted.audio,
          24000,
          24000
        );
        item.formatted.file = wavFile;
      }
      setItems(items);
    });

    if (realtimeClient) {
      setItems(realtimeClient.conversation.getItems());
    }

    return () => {
      // cleanup; resets to defaults
      realtimeClient?.reset();
    };
  }, [startTimer]);

  /**
   * Disconnect and reset conversation state
   */
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);

  const disconnectConversation = useCallback(async () => {
    const convoText = getConvoText();
    console.log('Convo Text:', convoText);

    const realtimeClient = realtimeClientRef.current;
    realtimeClient?.disconnect();

    const wavRecorder = wavRecorderRef.current;
    await wavRecorder.end();

    const wavStreamPlayer = wavStreamPlayerRef.current;
    await wavStreamPlayer.interrupt();
    stopTimer();

    setIsConnected(false);
    setRealtimeEvents([]);

    const summarizationCompletion = async () => {
      setIsLoadingFeedback(true);
      setIsInterstitialVisible(true);

      try {
        const response = await fetch('/aiproxy/summarize', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ transcript: convoText })
        });

        const responseJson = await response.json();
        setInterviewFeedback(responseJson.summary || '');
        setIsLoadingFeedback(false);
        setIsInterstitialVisible(false);
        navigate('/summary');
      } catch (error) {
        console.error('Error fetching OpenAI completion:', error);
        setIsLoadingFeedback(false);
        setIsInterstitialVisible(false);
      }
    };

    await summarizationCompletion();
    setItems([]);

    const response = await fetch('/aiproxy/init');
    if (response.ok) {
      const responseJson = await response.json();
      setRemainingTime(responseJson.credits);
    }
  }, [stopTimer, navigate, setInterviewFeedback]);

  /**
   * Auto-scroll the conversation logs
   */
  useEffect(() => {
    const conversationEls = [].slice.call(
      document.body.querySelectorAll('[data-conversation-content]')
    );
    for (const el of conversationEls) {
      const conversationEl = el as HTMLDivElement;
      conversationEl.scrollTop = conversationEl.scrollHeight;
    }
  }, [items]);

  /**
   * Set up render loops for the visualization canvas
   */
  useEffect(() => {
    let isLoaded = true;

    const wavRecorder = wavRecorderRef.current;
    const clientCanvas = clientCanvasRef.current;
    let clientCtx: CanvasRenderingContext2D | null = null;

    const wavStreamPlayer = wavStreamPlayerRef.current;
    const serverCanvas = serverCanvasRef.current;
    let serverCtx: CanvasRenderingContext2D | null = null;

    // Set canvas size to 240x240
    if (clientCanvas) {
      clientCanvas.width = 40;
      clientCanvas.height = 30;
    }

    if (serverCanvas) {
      serverCanvas.width = 240;
      serverCanvas.height = 240;
    }

    const render = () => {
      if (isLoaded) {

        // Assistant visualizer
        if (serverCanvas) {
          serverCtx = serverCtx || serverCanvas.getContext('2d');
          if (serverCtx) {
            serverCtx.clearRect(0, 0, serverCanvas.width, serverCanvas.height);
            const result = wavStreamPlayer.analyser
              ? wavStreamPlayer.getFrequencies('voice')
              : { values: new Float32Array([0]) };

            // Calculate the average amplitude from the frequency values
            const averageAmplitude = result.values.reduce((sum, value) => sum + value, 0) / result.values.length;

            // Use the average amplitude to scale the circle size
            const scaledRadius = Math.max(50, averageAmplitude * 10); // Adjust multiplier as needed

            WavRenderer.drawCircle(
              serverCanvas,
              serverCtx,
              result.values,
              'white',
              scaledRadius // Use the calculated scaled radius
            );
          }
        }

        // User visualizer
        if (clientCanvas) {
          clientCtx = clientCtx || clientCanvas.getContext('2d');
          if (clientCtx) {
            clientCtx.clearRect(0, 0, clientCanvas.width, clientCanvas.height);
            const result = wavRecorder.recording
              ? wavRecorder.getFrequencies('voice')
              : { values: new Float32Array([0]) };
            WavRenderer.drawBars(
              clientCanvas,
              clientCtx,
              result.values,
              'black',
              8,
              0,
              0
            );
          }
        }

        window.requestAnimationFrame(render);
      }
    };
    render();

    return () => {
      isLoaded = false;
    };
  }, []);

  const checkPermissions = async () => {
    try {
      const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      return result.state;
    } catch (error) {
      console.error('Error checking permissions:', error);
      return 'denied';
    }
  };

  useEffect(() => {
    // Show paywall immediately if user has no credits
    if (remainingTime === 0) {
      setIsInterstitialVisible(true);
    }
  }, [remainingTime]);

  /**
   * Render the application
   */
  return (
    <div data-component="ConsolePage">

      <main>

      { isSafari && (
        // Show specific instructions for Safari users
        <></>
      )}

        <div className="assistant">
          <Blob />
          {isConnected && (
            <div className="visualization-entry server">
              <canvas ref={serverCanvasRef} />
            </div>
          )}
        </div>

        <div className="visualization-entry client">
          <div className="timer">
            <span>{new Date(timer * 1000).toISOString().slice(11, 19)}</span>
          </div>
          <canvas ref={clientCanvasRef} />
        </div>

        <div className="interview-controls">

        {!isConnected && (
          <div className="form-block">
            <p>Enter a job description to start</p>
            <div className="input-wrapper">
              <input
                type="text"
                value={jobDescription}
                placeholder="ex. product manager at Meta"
                onChange={(e) => {
                  setjobDescription(e.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    if (jobDescription.trim() !== '') {
                      setIsInterstitialVisible(true);
                    } else {
                      alert('Please enter a job description.'); // Show error alert
                    }
                  }
                }}
              />
            </div>
          </div>
        )}

          <div className="content-actions">

          { isConnected ? (
            <Button
              label={'Stop Interview'}
              buttonStyle={'primary'}
              buttonSize={'lg'}
              onClick={ disconnectConversation }
            />
          ) : (
            <Button
              label={isConnected ? 'Stop Interview' : 'Start Interview'}
              buttonStyle={'primary'}
              buttonSize={'lg'}
              onClick={() => {
                // setIsInterstitialVisible(true);
                if (jobDescription.trim() !== '') {
                    setIsInterstitialVisible(true);
                } else {
                    alert('Please enter a job description.'); // Show error alert
                }
              }}
            />

          )}
          </div>
        </div>

        {/* Paywall or pre-interview screen */}
        {isInterstitialVisible && (
          <InterstitialPage
              setIsInterstitialVisible={setIsInterstitialVisible}
              remainingTime={remainingTime}
              connectConversation={connectConversation}
              profilePic={profilePic}
              isLoading={isLoadingFeedback}
          />
        )}

        <div className="convo" data-conversation-content style={{ display: isConvoVisible ? '' : 'none' }}>
          {!items.length && `awaiting connection...`}
          {items.map((conversationItem, i) => {
            return (
              <div className="conversation-item" key={conversationItem.id}>

                <div className={`speaker`} style={{textTransform: 'uppercase'}}>

                    {(
                      conversationItem.role || conversationItem.type
                    ).replaceAll('_', ' ')}:

                </div>
                <div className={`speaker-content`}>
                  {!conversationItem.formatted.tool &&
                    conversationItem.role === 'user' && (
                      <div>
                        {conversationItem.formatted.transcript ||
                          (conversationItem.formatted.audio?.length
                            ? '(awaiting transcript)'
                            : conversationItem.formatted.text ||
                              '(item sent)')}
                      </div>
                    )}
                  {!conversationItem.formatted.tool &&
                    conversationItem.role === 'assistant' && (
                      <div>
                        {conversationItem.formatted.transcript ||
                          conversationItem.formatted.text ||
                          '(truncated)'}
                      </div>
                    )}
                </div>
              </div>
            );
          })}
        </div>

{process.env.NODE_ENV === 'development' && (
        <div className="admin">
          <button
            onClick={() => navigate('/summary')}
          >
            Go to Summary Page
          </button>
          <button
            onClick={() => {
              setRemainingTime(0);
              setIsInterstitialVisible(true);
            }}
          >
            Show Paywall
          </button>
          <label>
            <input
              type="checkbox"
              checked={isConvoVisible}
              onChange={(e) => setIsConvoVisible(e.target.checked)}
            />
            Show Conversation
          </label>
        </div>
)}

      </main>
    </div>
  );
}
